<template>
  <div>
    <h2>{{ $t("All Domains") }}</h2>
    <b-row class="mb-5">
      <b-col>
        <t-data-table
          data-test-id="domains-data-table"
          with-filter
          :filter-placeholder="$t('Search Domains')"
          :loading="loading"
          :table-data="dataSet"
          :actions="tableActions"
          user-role="customer"
          @on-delete="onDelete"
          @on-add="onAdd"
          @on-export="onExport"
          @on-row-clicked="onRowClicked"
          :searchExports="searchExports"
          @export-headers="exportedHeaders"
        />
      </b-col>
    </b-row>
  </div>
</template>

<route>
{
  "name": "Customer Domains",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

export default {
  layout: "customer",
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("customerDomains", [
      "fetchCustomerDomainList",
      "deleteCustomerDomainListEntrys",
    ]),
    ...mapActions("customerPolicies", ["fetchCustomerPolicies"]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    async onDelete(domains) {
      try {
        await this.deleteCustomerDomainListEntrys({
          userId: this.selectedUser.id,
          domains,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: domains.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    onAdd({ name, modal, type }) {
      this.createModal({
        modal,
        title: name,
        dataTestId: `modal-add-domain-${type}`,
        subtitle: this.selectedUser.accountname,
        modalData: {
          type,
          customerPolicies: this.customerPolicies,
          toPolicy: false,
          policies: this.customerPolicies,
        },
        props: {
          size: "lg",
          okTitle: `${
            type === "allow" ? this.$t("Allow") : this.$t("Block")
          } ${this.$t("Domain")}`,
        },
      });

      this.showModal();
    },
    onImport(items) {
      console.log("Import items:", items); // eslint-disable-line no-console
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });
          return payload;
        });
        this.$downloadCSV(exportData, "Domains");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No Domains in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onRowClicked(item) {
      const policy =
        !item.policyname || item.policyname === "Default"
          ? "default"
          : item.policyid;

      this.$router.push({
        path: `/customer/content_filtering/domains/${item.id}?t=${item.object}&p=${policy}`,
      });
    },
  },
  computed: {
    ...mapGetters("customerDomains", ["customerDomainList"]),
    ...mapGetters("customerPolicies", ["customerPolicies"]),
    ...mapGetters("authentication", ["selectedUser"]),
  },
  data() {
    return {
      loading: true,
      currentHeaders: null,

      searchExports: ["domain", "object", "policyname"],
      dataSet: {
        fields: [
          { value: "domain", text: this.$t("Domain"), sortable: true },
          { value: "object", text: this.$t("Status"), sortable: true },
          { value: "subdomains", text: this.$t("Subdomains"), sortable: true },
          {
            value: "log_requests",
            text: this.$t("Log Requests"),
            sortable: true,
          },
          { value: "policyname", text: this.$t("Policy"), sortable: true },
          { value: "comment", text: this.$t("Comments"), sortable: true },
        ],
        items: [],
      },
      tableActions: {
        add: {
          enabled: true,
          label: this.$t("Add Domain"),
          actions: [
            {
              text: this.$t("Allow a Domain"),
              value: {
                name: this.$t("Allow a Domain"),
                modal: "t-add-domain",
                type: "allow",
              },
            },
            {
              text: this.$t("Block a Domain"),
              value: {
                name: this.$t("Block a Domain"),
                modal: "t-add-domain",
                type: "block",
              },
            },
          ],
        },
        import: { enabled: false },
        export: { enabled: true },
        delete: { enabled: true },
      },
    };
  },
  watch: {
    customerDomainList() {
      this.dataSet.items = this.customerDomainList;
    },
  },
  async created() {
    this.setPageTitle(this.$t("Domains"));
    this.setPageSubtitle([
      this.$t(
        "Allow access to domains that are currently being blocked by a policy, or block domains that are currently being allowed."
      ),
    ]);
    await Promise.all([
      this.fetchCustomerDomainList({ id: this.selectedUser.id }),
      this.fetchCustomerPolicies({
        id: this.selectedUser.id,
        show_default: "false",
        results: 10,
        sortDesc: [false],
      }),
    ]);

    setTimeout(() => {
      this.loading = false;
    }, 500);
    this.dataSet.items = this.customerDomainList;
  },
};
</script>
